import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import type { CropData } from "../lib/utils";

const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];

export type ImageSlotConfig = {
  maxFileSize: number;
  outputWidth: number;
  outputHeight: number;
  aspect: number;
  shape?: "rect" | "round";
};

export type ImageSlotState = {
  inputRef: React.RefObject<HTMLInputElement | null>;
  preview: string | null;
  displayUrl: string | null;
  sourceUrl: string | null;
  cropData: CropData | null;
  setPendingSourceFile: (file: File | null) => void;
  setPendingBlob: (blob: Blob | null) => void;
  setPreview: (url: string | null) => void;
  setCropData: (data: CropData | null) => void;
};

/**
 * Encapsulates the crop-modal interaction pattern used across settings pages.
 *
 * Manages: source blob URLs, crop modal state, file-select/image-click handlers.
 * The caller keeps ownership of per-slot form state (pending files, previews, crop data)
 * and passes setter callbacks so the hook can update them on crop confirm.
 */
export function useImageCrop<T extends string>(
  config: Record<T, ImageSlotConfig>,
  slots: Record<T, ImageSlotState>,
) {
  const sourceRefs = useRef<Partial<Record<T, string>>>({});
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [activeSlot, setActiveSlot] = useState<T | null>(null);
  const [cropInitialData, setCropInitialData] = useState<CropData | null>(null);

  const previewRefs = useRef<Partial<Record<T, string>>>({});

  // Cleanup source and preview blob URLs on unmount
  useEffect(() => {
    return () => {
      for (const url of Object.values(sourceRefs.current)) {
        if (url) URL.revokeObjectURL(url as string);
      }
      for (const url of Object.values(previewRefs.current)) {
        if (url) URL.revokeObjectURL(url as string);
      }
    };
  }, []);

  /** Get computed display src for a slot (preview ?? displayUrl) */
  const getSrc = (slot: T): string | null => {
    return slots[slot].preview ?? slots[slot].displayUrl ?? null;
  };

  /** Handle file input change — validates, stores source ref, opens crop modal */
  const handleFileSelect = (slot: T) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error("Unsupported file type. Please use PNG, JPEG, or WebP.");
      return;
    }
    if (file.size > config[slot].maxFileSize) {
      const mb = (config[slot].maxFileSize / 1024 / 1024).toFixed(0);
      toast.error(`File is too large. Maximum size is ${mb} MB.`);
      return;
    }

    const old = sourceRefs.current[slot];
    if (old) URL.revokeObjectURL(old);

    const url = URL.createObjectURL(file);
    sourceRefs.current[slot] = url;
    slots[slot].setPendingSourceFile(file);

    setCropInitialData(null);
    setCropSrc(url);
    setActiveSlot(slot);
    e.target.value = "";
  };

  /** Handle click on image preview — opens crop modal if source exists, else triggers file picker */
  const handleImageClick = (slot: T) => {
    const src = sourceRefs.current[slot] ?? slots[slot].sourceUrl ?? slots[slot].displayUrl;
    if (src) {
      setCropInitialData(slots[slot].cropData);
      setCropSrc(src);
      setActiveSlot(slot);
    } else {
      slots[slot].inputRef.current?.click();
    }
  };

  /** Handle crop modal confirm — updates form state via slot setters */
  const handleCropConfirm = (result: { blob: Blob; cropData: CropData }) => {
    if (!activeSlot) return;
    const slot = slots[activeSlot];

    if (slot.preview) URL.revokeObjectURL(slot.preview);

    const previewUrl = URL.createObjectURL(result.blob);
    previewRefs.current[activeSlot] = previewUrl;
    slot.setPendingBlob(result.blob);
    slot.setPreview(previewUrl);
    slot.setCropData(result.cropData);

    setCropSrc(null);
    setActiveSlot(null);
  };

  const handleCropClose = () => {
    setCropSrc(null);
    setActiveSlot(null);
  };

  // Props to spread onto <ImageCropModal>, or null when closed
  const cropModalProps =
    cropSrc && activeSlot
      ? {
          open: true as const,
          onClose: handleCropClose,
          imageSrc: cropSrc,
          aspect: config[activeSlot].aspect,
          shape: (config[activeSlot].shape ?? "rect") as "rect" | "round",
          outputWidth: config[activeSlot].outputWidth,
          outputHeight: config[activeSlot].outputHeight,
          onCropConfirm: handleCropConfirm,
          initialCropData: cropInitialData,
        }
      : null;

  return {
    getSrc,
    handleFileSelect,
    handleImageClick,
    cropModalProps,
  };
}
